import type { PresentationBlock, PresentationOptionV2 } from 'paintscout'

/**
 * How a block's text relates to the company's site, as the review marks show it (#24250).
 * - `ai`: the model wrote it (`provenance: 'ai_generated'`).
 * - `edited`: the model condensed the site's copy (`provenance: 'ai_summarized'`).
 */
export type ReviewLevel = 'ai' | 'edited'

export interface PendingReview {
  blockKey: string
  /** Formik path of the block's `advanced.reviewed` flag. */
  reviewedPath: string
}

/** The block's mark, or null when it is the company's own words or the user's (no provenance). */
export function getBlockReviewLevel(block?: Pick<PresentationBlock, 'advanced'>): ReviewLevel | null {
  switch (block?.advanced?.provenance) {
    case 'ai_generated':
      return 'ai'
    case 'ai_summarized':
      return 'edited'
    default:
      return null
  }
}

export function isBlockReviewPending(block?: Pick<PresentationBlock, 'advanced'>): boolean {
  return getBlockReviewLevel(block) !== null && !block?.advanced?.reviewed
}

/** Every block in the document, on every page, that carries a mark the user has not cleared. */
export function getPendingReviews(presentation?: Pick<PresentationOptionV2, 'pages'>): PendingReview[] {
  const pending: PendingReview[] = []

  ;(presentation?.pages ?? []).forEach((page, pageIndex) => {
    ;(page.sections ?? []).forEach((section, sectionIndex) => {
      ;(section.blocks ?? []).forEach((block, blockIndex) => {
        if (!isBlockReviewPending(block)) return
        pending.push({
          blockKey: block.key,
          reviewedPath: `pages[${pageIndex}].sections[${sectionIndex}].blocks[${blockIndex}].advanced.reviewed`
        })
      })
    })
  })

  return pending
}
